import "react-responsive-carousel/lib/styles/carousel.min.css";
import "swiper/css";
import "swiper/css/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import { useNavigate, Link } from "react-router-dom";
import { FaAngleRight, FaArrowRight } from "react-icons/fa";
import RecentSoldPropertySlider from "./RecentSoldPropertySlider";
import React, { useState } from "react";
import { useRecentlySoldProperties } from "../../../Hooks/useRecentlySoldProperties/useRecentlySoldProperties";
import { AuthModal } from "../../../models/AuthModal";
import { CollectionPickerModal } from "../../Collections/CollectionPickerModal";
import CollectionCreateModal from "../../Collections/CollectionCreateModal";
import { Home } from "lucide-react";

function RecentSoldSkeleton() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 animate-pulse w-full">
      <div className="w-full h-48 sm:h-52 bg-gray-200" />
      <div className="p-4 sm:p-5 space-y-3">
        <div className="h-5 bg-gray-200 rounded w-3/4" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
        <div className="flex gap-2 pt-2">
          <div className="h-3 w-12 bg-gray-200 rounded" />
          <div className="h-3 w-12 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  );
}

const RecentSoldEmptyState = ({ browseTo }) => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[260px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F3F6F9] flex items-center justify-center mb-5 ring-1 ring-[#EAEAEA]"
      aria-hidden
    >
      <Home className="text-[#097FE6]" size={36} strokeWidth={1.75} />
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      No sold listings to show
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      There aren&apos;t any properties in this list right now. Explore listings to find your next home.
    </p>
    <Link to={browseTo} className="w-full sm:w-auto">
      <button
        type="button"
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Browse properties
      </button>
    </Link>
  </div>
);

const RecentSoldErrorState = ({ onRetry, browseTo = "/sold" }) => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[260px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#FEF2F2] flex items-center justify-center mb-5 ring-1 ring-[#FECACA]"
      aria-hidden
    >
      <span className="text-2xl sm:text-3xl font-semibold text-[#DC2626]" aria-hidden>
        !
      </span>
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      Couldn&apos;t load properties
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      Something went wrong while fetching the list. Check your connection and try again.
    </p>
    {typeof onRetry === "function" ? (
      <button
        type="button"
        onClick={onRetry}
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Try again
      </button>
    ) : (
      <Link to={browseTo} className="w-full sm:w-auto">
        <button
          type="button"
          className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
        >
          Browse properties
        </button>
      </Link>
    )}
  </div>
);

const RecentSoldProperties = ({ limit = 10, browseTo = "/sold" }) => {
  const navigate = useNavigate();
  const [AuthPopUp, setAuthPopUp] = useState(false);
  const isAuthenticate = localStorage.getItem("Token");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const {
    data: soldProperties,
    loading: isLoading,
    error: fetchError,
    refetch,
  } = useRecentlySoldProperties(limit);

  const list = Array.isArray(soldProperties) ? soldProperties : [];
  const showCarousel = !isLoading && !fetchError && list.length > 0;

  const handleFavoriteClick = (prop) => {
    if (!prop) return;
    setSelectedProperty(prop);
    setPickerOpen(true);
  };

  const swiperBreakpoints = {
    640: {
      slidesPerView: 2,
      spaceBetween: 20,
    },
    1024: {
      slidesPerView: 3,
      spaceBetween: 24,
    },
    1280: {
      slidesPerView: 3,
      spaceBetween: 24,
    },
    1536: {
      slidesPerView: 4,
      spaceBetween: 24,
    },
  };

  return (
    <>
      <div className="bg-white py-10 sm:py-15 lg:py-20">
        <div className="container">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 mb-10">
            <div className="flex flex-col gap-3 sm:gap-4 items-start justify-start max-w-xl">
              <h3 className="font-bold text-xl sm:text-2xl lg:text-3xl xl:text-4xl text-[#000929]">
                Recently sold properties
              </h3>
            </div>
            {showCarousel ? (
              <div className="hidden md:flex items-center gap-3">
                <button
                  type="button"
                  className="custom-prev w-9 h-9 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center bg-white shadow"
                >
                  <FaArrowRight color="#097FE6" className="rotate-180" />
                </button>
                <button
                  type="button"
                  className="custom-next w-9 h-9 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center bg-white shadow"
                >
                  <FaArrowRight color="#097FE6" />
                </button>
              </div>
            ) : null}
          </div>
          <div className="flex justify-center">
            <div className="w-full md:hidden flex flex-col gap-4">
              {isLoading ? (
                <>
                  <RecentSoldSkeleton />
                  <RecentSoldSkeleton />
                  <RecentSoldSkeleton />
                </>
              ) : fetchError ? (
                <RecentSoldErrorState onRetry={refetch} browseTo={browseTo} />
              ) : list.length === 0 ? (
                <RecentSoldEmptyState browseTo={browseTo} />
              ) : (
                list.map((item, index) => (
                  <RecentSoldPropertySlider
                    key={item?._id ?? item?.id ?? `sold-${index}`}
                    property={item}
                    isAuthenticate={isAuthenticate}
                    setAuthPopUp={setAuthPopUp}
                    onFavoriteClick={handleFavoriteClick}
                  />
                ))
              )}

              {!isLoading && !fetchError && list.length > 10 && (
                <div
                  className="flex justify-center items-center py-4"
                  onClick={() => navigate(browseTo)}
                >
                  <div className="bg-[#097FE6] text-white px-6 py-3 rounded-full flex justify-center items-center gap-2 cursor-pointer font-medium">
                    <span className="text-sm">Show all the property</span>
                    <FaAngleRight size={18} />
                  </div>
                </div>
              )}
            </div>
            <div className="hidden md:block w-full">
              {isLoading ? (
                <Swiper loop={false} spaceBetween={24} slidesPerView={1} breakpoints={swiperBreakpoints} className="w-full">
                  {[0, 1, 2, 3].map((i) => (
                    <SwiperSlide key={i}>
                      <RecentSoldSkeleton />
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : fetchError ? (
                <RecentSoldErrorState onRetry={refetch} browseTo={browseTo} />
              ) : list.length === 0 ? (
                <RecentSoldEmptyState browseTo={browseTo} />
              ) : (
                <Swiper
                  loop={false}
                  spaceBetween={24}
                  slidesPerView={1}
                  modules={[Navigation]}
                  navigation={{
                    prevEl: ".custom-prev",
                    nextEl: ".custom-next",
                  }}
                  breakpoints={swiperBreakpoints}
                  className="w-full"
                >
                  {list.map((item, index) => (
                    <SwiperSlide key={item?._id ?? item?.id ?? `sold-${index}`}>
                      <RecentSoldPropertySlider
                        property={item}
                        isAuthenticate={isAuthenticate}
                        setAuthPopUp={setAuthPopUp}
                        onFavoriteClick={handleFavoriteClick}
                      />
                    </SwiperSlide>
                  ))}
                  {list.length > 10 ? (
                    <SwiperSlide key="show-more">
                      <div className="flex justify-start items-center h-[100%] px-3">
                        <div
                          className="bg-blue-100 p-3 rounded-full cursor-pointer flex justify-center items-center"
                          onClick={() => navigate(browseTo)}
                        >
                          <p className="text-sm pe-2">See More</p>
                          <div className="p-2 rounded-full bg-blue-500">
                            <FaAngleRight color="white" size={18} />
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ) : null}
                </Swiper>
              )}
            </div>
          </div>
        </div>
      </div>

      {AuthPopUp && (
        <AuthModal open={AuthPopUp} onClose={() => setAuthPopUp(false)} />
      )}

      {pickerOpen && selectedProperty && (
        <CollectionPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          propertyId={selectedProperty?._id || selectedProperty?.id}
          initialSelectedIds={
            selectedProperty?.collections?.map(
              (col) => col.id ?? col._id ?? col.collectionId,
            ) || []
          }
          onCreateNewCollection={() => setCreateModalOpen(true)}
        />
      )}
      {createModalOpen && (
        <CollectionCreateModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          editingCollection={null}
          onSaved={() => { }}
        />
      )}
    </>
  );
};

export default RecentSoldProperties;
